// Schema-driven definitions of the four GFM work permits.
// field types: text, textarea, date, time, radio, confirm, matrix
const yn = ["Yes", "No"];
const yna = ["Yes", "No", "N/A"];
const ynna = ["Yes", "N/A"];

export const GFM_SITES = [
  "Hamer with Springside School / M6 Theatre",
  "Hollin with Newlands School",
  "Marland Hill School",
  "Oulder Hill School",
  "Oulder Hill Leisure Centre",
  "Redwood School",
];

export const PERMIT_TYPES = [
  { type: "site_induction", title: "Site Induction", short: "SI", color: "#007AFF" },
  { type: "control_of_access", title: "Control of Access", short: "CA", color: "#34C759" },
  { type: "general_hazards", title: "General Hazardous Permit", short: "GH", color: "#FFCC00" },
  { type: "hot_works", title: "Hot Works Permit", short: "HW", color: "#FF3B30" },
];

export function permitMeta(type) {
  return PERMIT_TYPES.find((p) => p.type === type);
}

// The three issuable work permits (site induction is handled at contractor level).
export const WORK_PERMIT_TYPES = PERMIT_TYPES.filter((p) => p.type !== "site_induction");

const CONTROL_OF_ACCESS = {
  type: "control_of_access",
  title: "Control of Access",
  short: "CA",
  color: "#34C759",
  intro:
    "This document must not be used in place of a GFM Hot Work Permit / GFM General Hazardous Permit where these are required, or where the work activity is not defined as low risk.",
  sections: [
    {
      id: "auth",
      title: "Section 1 — Authorisation (GFM Authorised Person)",
      fields: [
        { key: "inducted", label: "Has this individual been inducted?", type: "radio", options: yn, note: "If no, this individual must be inducted first." },
        { key: "rams_reviewed", label: "Have you reviewed their RAMS?", type: "radio", options: yna, note: "RAMS = Risk Assessments & Method Statements (incl. COSHH)." },
        { key: "rams_na_reason", label: "If RAMS are N/A, detail why", type: "text" },
        { key: "work_location", label: "Detail work location/s", type: "textarea" },
        { key: "work_summary", label: "Summary of work being undertaken", type: "textarea" },
        { key: "lone_working", label: "Are lone working controls in place?", type: "radio", options: ynna },
        { key: "known_hazards", label: "Known hazards or additional safety requirements", type: "textarea" },
        { key: "cert_expiry_checked", label: "Have you checked certificate expiry dates for equipment?", type: "radio", options: ynna, note: "Consider PAT, Calibration etc." },
        { key: "competency_verified", label: "Have you verified the individual's competency?", type: "radio", options: ["Yes"], note: "If no, this must be done first." },
        { key: "c1", label: "I confirm as the Authorised Person that I have authority to allow the works to commence.", type: "confirm" },
        { key: "c2", label: "I confirm as the Authorised Person that the work activity is low risk.", type: "confirm" },
        { key: "c3", label: "I have visited the location and explained the limits of the work activity with the Person being granted access.", type: "confirm" },
        { key: "c4", label: "I have explained/shown where applicable the hazards/additional safety instructions detailed above.", type: "confirm" },
        { key: "c5", label: "I hold copies of the RAMS and other relevant documents for the work activity being undertaken.", type: "confirm" },
      ],
    },
    {
      id: "accept",
      title: "Section 2 — Acceptance (Person in Charge)",
      fields: [
        { key: "a1", label: "I confirm as the Person in Charge that I understand the scope of work for which access is granted.", type: "confirm" },
        { key: "a2", label: "I understand the known hazards and additional safety instructions in Section 1.", type: "confirm" },
        { key: "a3", label: "I am responsible for the work activity, and others within the work party.", type: "confirm" },
        { key: "a4", label: "I accept that the GFM Authorised Person has the right to stop / cancel the work activity.", type: "confirm" },
        { key: "a5", label: "I will stop the work if unforeseen matters arise that compromise safety, and advise the GFM Authorised Person.", type: "confirm" },
        { key: "a6", label: "I will ensure the areas are left clean, tidy and safe, and that rubbish is removed from site.", type: "confirm" },
        { key: "a7", label: "I will always hold a copy of this document and return it to the GFM Authorised Person at the end of the works.", type: "confirm" },
      ],
    },
  ],
};

const GENERAL_HAZARDS = {
  type: "general_hazards",
  title: "General Hazardous Permit",
  short: "GH",
  color: "#FFCC00",
  intro: "This document must not be used in place of a GFM Hot Work Permit where these are required.",
  sections: [
    {
      id: "auth",
      title: "Section 1 — Authorisation (GFM Authorised Person)",
      fields: [
        { key: "inducted", label: "Has this individual been inducted?", type: "radio", options: yn },
        { key: "rams_reviewed", label: "Have you reviewed their RAMS?", type: "radio", options: yn },
        {
          key: "work_activity",
          label: "Work Activity",
          type: "matrix",
          columns: ["Yes", "N/A"],
          rows: [
            { label: "Will fire systems be taken off line for maintenance?" },
            { label: "Will MEWP's be used?" },
            { label: "Will work be on unprotected roofs or elevations?" },
            { label: "Will mobile scaffolds be used?" },
            { label: "Will work be within lift shafts?" },
            { label: "Will work utilise Mansafe systems?" },
            { label: "Will work be on or near fragile surfaces / un-boarded lofts?" },
            { label: "Will work involve ladders and step ladders?" },
            { label: "Other (detail below)" },
          ],
        },
        { key: "work_location", label: "Detail work location/s", type: "textarea" },
        { key: "work_summary", label: "Summary of work being undertaken", type: "textarea" },
        { key: "lone_working", label: "Are lone working controls in place?", type: "radio", options: ynna },
        { key: "competency_verified", label: "Have you verified the individual's competency?", type: "radio", options: ["Yes"] },
        { key: "known_hazards", label: "Known hazards, additional safety requirements or system isolations undertaken", type: "textarea" },
        { key: "c1", label: "I confirm as the Authorised Person that I have authority to allow the works to commence.", type: "confirm" },
        { key: "c2", label: "I have visited the location and explained the limits of the work activity with the PIC.", type: "confirm" },
        { key: "c3", label: "I have explained/shown where applicable the hazards/additional safety instructions and isolations detailed above.", type: "confirm" },
        { key: "c4", label: "I hold copies of the RAMS, completed General Hazardous checklist and other relevant documents.", type: "confirm" },
      ],
    },
    {
      id: "accept",
      title: "Section 2 — Acceptance (Person in Charge)",
      fields: [
        { key: "a1", label: "I confirm as the PIC that I understand the scope of work for which the permit is issued.", type: "confirm" },
        { key: "a2", label: "I understand the known hazards and additional safety instructions in Section 1.", type: "confirm" },
        { key: "a3", label: "I am responsible for the work activity, and others within the work party.", type: "confirm" },
        { key: "a4", label: "I accept that the GFM Authorised Person has the right to stop / cancel the work activity.", type: "confirm" },
        { key: "a5", label: "I will stop the work if unforeseen matters arise that compromise safety, and advise the GFM Authorised Person.", type: "confirm" },
        { key: "a6", label: "I will ensure the areas are left clean, tidy and safe, and that waste is removed from site.", type: "confirm" },
        { key: "a7", label: "I will always hold a copy of this document and return it to the GFM Authorised Person at the end of the works.", type: "confirm" },
      ],
    },
  ],
};

const HOT_WORKS = {
  type: "hot_works",
  title: "Hot Works Permit",
  short: "HW",
  color: "#FF3B30",
  intro: "A hot work permit must be issued for all tasks that involve the use of naked flames, or where work activities create sparks or excessive heat.",
  sections: [
    {
      id: "auth",
      title: "Section 1 — Authorisation (GFM Authorised Person)",
      fields: [
        { key: "inducted", label: "Has this individual been inducted?", type: "radio", options: yn },
        { key: "sssow_reviewed", label: "Have you reviewed the Subcontractor's Safe System of Work and Risk Assessment/s (incl. COSHH if applicable)?", type: "radio", options: yn },
        { key: "work_location", label: "Detail work location/s", type: "textarea" },
        { key: "work_summary", label: "Summary of work being undertaken", type: "textarea" },
        {
          key: "fire_safety",
          label: "Fire Safety Requirements Check List",
          type: "matrix",
          columns: ["Yes", "No", "N/A"],
          rows: [
            { label: "Is the Fire Responsible Person aware of the work?" },
            { label: "Work Area is not a \u201cHot Work Prohibited\u201d Area?" },
            { label: "Have combustibles been removed / covered with fire sheeting?" },
            { label: "Has the fire alarm zone/s been isolated?" },
            { label: "Any flammable liquids removed?" },
            { label: "If applicable, automatic sprinkler fully operational?" },
            { label: "Is the fire watch in place?" },
            { label: "Are suitable warning signs posted?" },
            { label: "Do the fire watch personnel know how to raise the alarm?" },
            { label: "Is ventilation required for fume control?" },
            { label: "Are suitable type/number of Fire Fighting Equipment in place?" },
            { label: "Do other fire systems need isolation?" },
            { label: "Has hot work equipment been thoroughly checked?" },
            { label: "Is the Occupier aware of the work?" },
          ],
        },
        { key: "systems_detail", label: "If other fire systems need isolation, detail systems here", type: "textarea" },
        { key: "known_hazards", label: "Known hazards or additional safety requirements", type: "textarea" },
        { key: "c1", label: "I confirm as the GFM Authorised Person that I have authority to allow the works to commence.", type: "confirm" },
        { key: "c2", label: "I have visited the location and explained the limits of the work activity with the Person In Charge.", type: "confirm" },
        { key: "c3", label: "I have explained/shown where applicable the hazards/additional safety instructions detailed above.", type: "confirm" },
        { key: "c4", label: "I hold copies of the Safe Systems of Work, Risk Assessments and any other relevant documents.", type: "confirm" },
      ],
    },
    {
      id: "accept",
      title: "Section 2 — Acceptance (Person in Charge)",
      note: "The expiry date/time must be 1 hour less than the end of the working day to allow for fire watch.",
      fields: [
        { key: "a1", label: "I confirm as the Person in Charge that I understand the scope of work for which the permit is issued.", type: "confirm" },
        { key: "a2", label: "I understand the known hazards and additional safety instructions in Section 1.", type: "confirm" },
        { key: "a3", label: "I am responsible for the work activity, and others within the work party.", type: "confirm" },
        { key: "a4", label: "I understand that I must undertake a 60-minute fire watch after works have been completed / stopped.", type: "confirm" },
        { key: "a5", label: "I accept that the GFM Authorised Person has the right to stop / cancel the work activity.", type: "confirm" },
        { key: "a6", label: "I will stop the work if unforeseen matters arise that compromise safety, and advise the GFM Authorised Person.", type: "confirm" },
        { key: "a7", label: "I will ensure the areas are left clean, tidy and safe, and that rubbish is removed from site.", type: "confirm" },
        { key: "a8", label: "I will always hold a copy of this document and return it to the GFM Authorised Person at the end of the works.", type: "confirm" },
      ],
    },
  ],
};

const SITE_2A_ITEMS = [
  ["Asbestos", "Site Asbestos Register must be consulted. No work on asbestos. Report any suspected asbestos."],
  ["Access", "No work in confined spaces without a permit. Working at height access by General Hazardous Permit."],
  ["Accident / Incident Reporting", "To the GFM representative on site."],
  ["Alcohol / Drugs", "Not allowed on site. Report any incidences."],
  ["Behaviour", "Responsible behaviour at all times incl. respect to occupiers, noise, horseplay etc."],
  ["Children, Vulnerable & Mentally", "Be aware of vulnerable people; do not be left alone with anyone in these categories."],
  ["Confidentiality", "Respect confidentiality of occupiers. Do not scan notes or computer screens."],
  ["COSHH", "Only use/move chemicals you are trained to; work within the COSHH risk assessment."],
  ["Dress Code", "Suitable attire incl. required PPE; no shirtless working."],
  ["Dust / Fumes", "Only by agreement; protect self and others, limit to minimum, clean up."],
  ["Eating / Drinking / WCs / Washing", "Use facilities agreed with GFM; keep clean if shared."],
  ["Electrical", "Equipment must be in test. New/amended circuits certificated. Lock off when isolated."],
  ["Live Electrical Work", "GFM policy: no live electrical working at any time."],
  ["Fire procedures", "Adhere to fire procedures briefed and any Hot Work Permit issued."],
  ["Fire Compartmentalisation", "Walls/ceilings forming a fire compartment must be reinstated to correct standard."],
  ["First Aid", "Informed of site first aid facilities; ask what arrangements they have."],
  ["Hours of Work", "Agree hours; ensure the hour after hot work is built in."],
  ["Infection Control", "Explain good hand washing, especially in healthcare settings."],
  ["Interruption of Services", "No utilities interrupted without written agreement."],
  ["Key Issue / Card Access", "Against signature and handed back each day."],
  ["Known Site Hazards", "Asbestos, occupiers, nesting birds, difficult access etc."],
  ["Lone Working", "Agree procedure."],
  ["Manual Handling", "Contractor cannot rely on others to man-handle equipment they bring on site."],
  ["MEWPS / Mobile Towers", "By agreement; operators and equipment in-date certificates checked."],
  ["Mobile phones", "Use with permission only; photo facility with permission only."],
  ["Noise / Vibration", "Agreed and limited to agreed duration/time with precautions."],
  ["Parking", "By agreement in designated areas."],
  ["Permits To Work", "Induction and general work permit for access; Confined Space, Asbestos and Hot Work Permits as required."],
  ["Personal Alarms", "If issued, use explained and returned at end of each day."],
  ["Personal possessions", "Responsibility of the contractor; some may be restricted."],
  ["PPE", "All required PPE from RA/MS/SSoW must be used and in good order."],
  ["Public", "Public suitably separated from work areas (barriers, signs etc.)."],
  ["Risk Assessments & Method Statement / SSoW", "Site-specific RA/MS/SSoW required; generic ones not acceptable."],
  ["Roof Access / Loft Access", "Prohibited without specific agreement on RAMS and fall restriction."],
  ["Site Contacts", "Provide phone numbers of main contacts, GFM Reception, Security etc."],
  ["Smoking", "Not allowed except in designated areas; never in GFM-only areas."],
  ["Storage", "Storage of materials/equipment by agreement with GFM and occupiers."],
  ["Subcontractor's booklet", "Issue and obtain signature (L3 Sub-Contractors Site rules)."],
  ["Tidiness", "Work areas kept tidy; clean as you go and end-of-day tidying."],
  ["Training / Competency", "Certificates/membership of competency schemes produced by contractor."],
  ["Tools", "Tools suitable for task; battery tools preferred; kept under supervision."],
  ["Use of Cameras", "Cameras (incl. phone cameras) only with express permission."],
  ["Waste", "Contractor removes all waste and is suitably licensed to do so."],
];

const SITE_INDUCTION = {
  type: "site_induction",
  title: "Site or Activity Induction",
  short: "SI",
  color: "#007AFF",
  sections: [
    {
      id: "personal",
      title: "Part 1 — Inductee Details",
      fields: [
        { key: "first_name", label: "First name", type: "text" },
        { key: "last_name", label: "Surname", type: "text" },
        { key: "job_title", label: "Job Title", type: "text" },
        { key: "mobile", label: "Mobile number", type: "text" },
        { key: "email", label: "Email address", type: "text" },
        { key: "dob", label: "Date of birth", type: "date" },
        { key: "emergency_contact", label: "Nominated contact in case of emergency", type: "text" },
        { key: "emergency_number", label: "Nominated contact in case of emergency number", type: "text" },
        { key: "dbs_number", label: "Enhanced DBS number", type: "text" },
        { key: "certification_date", label: "Certification date", type: "date" },
        { key: "employers_address", label: "Employers Address", type: "textarea" },
        { key: "employers_contact", label: "Employers contact number", type: "text" },
        { key: "employers_manager", label: "Employers contact manager", type: "text" },
        { key: "medical", label: "Do you have any medical conditions that the site needs to be aware of?", type: "textarea" },
      ],
    },
    {
      id: "site_info",
      title: "Part 2 — Site Information Checklist",
      fields: [
        { key: "assembly_point", label: "GFM assembly point location", type: "text" },
        {
          key: "site_info",
          label: "Site Information",
          type: "matrix",
          columns: ["Applicable", "Inducted"],
          rows: [
            { label: "Location of fire exits, assembly points, toilet & welfare facilities." },
            { label: "Access/Egress supervised by onsite GFM Manager; access card handed back daily." },
            { label: "Identification card must be worn at all times where required; may be searched." },
            { label: "May be required to wear a personal alarm at all times." },
            { label: "Permission required prior to entering a restricted area." },
            { label: "May be required to be escorted by a GFM engineer." },
          ],
        },
        {
          key: "skills",
          label: "Skills and Training",
          type: "matrix",
          columns: ["Applicable", "Checked"],
          rows: ["First Aid", "Gas Safe", "Electrical", "Abrasive Wheels", "Cartridge Tools", "Unvented Hot Water", "MEWP", "Scaffold Tower", "CSCS", "Welding"].map((l) => ({ label: l })),
        },
        {
          key: "ppe",
          label: "PPE",
          type: "matrix",
          columns: ["Applicable", "Issued/Own"],
          rows: ["Eye Protection", "Respiratory Protection", "Head Protection", "Ear Protection", "Safety Footwear", "Hand Protection", "Workwear / Overall / Coat", "Face Mask"].map((l) => ({ label: l })),
        },
        {
          key: "general",
          label: "General",
          type: "matrix",
          columns: ["Applicable", "Inducted"],
          rows: ["Fire Emergency Procedures", "Access/Egress Routes", "Welfare Facilities"].map((l) => ({ label: l })),
        },
      ],
    },
    {
      id: "brief",
      title: "Part 3 — Site Specific Brief",
      fields: [
        {
          key: "brief",
          label: "Site specific brief",
          type: "matrix",
          columns: ["Applicable", "N/A", "Inducted"],
          rows: SITE_2A_ITEMS.map(([label, desc]) => ({ label, desc })),
        },
      ],
    },
    {
      id: "acceptance",
      title: "Part 4 — Inductee Acceptance",
      fields: [
        { key: "accept", label: "As the inductee I confirm all the information I have provided is correct as dated on this induction, and that if any information changes I will notify GFM and update the information in the portal. I will also be happy to be contacted should any concerns arise.", type: "confirm" },
      ],
    },
  ],
};

export const SCHEMAS = {
  site_induction: SITE_INDUCTION,
  control_of_access: CONTROL_OF_ACCESS,
  general_hazards: GENERAL_HAZARDS,
  hot_works: HOT_WORKS,
};
